import React, { useState, useEffect } from 'react';
import {
    HeartPulse,
    PersonStanding,
    Move,
    Activity,
    Pill,
    Utensils,
    Bandage,
    Droplets,
    Brain,
    Scale,
    ShieldCheck,
    Home,
    Flame,
    ShieldAlert,
    Wrench,
    MapPin,
    Wallet,
    HeartHandshake,
    ClipboardList,
    ChevronDown,
    Sparkles,
    User,
    Plus,
} from 'lucide-react';
import Modal from '../../../components/Modal';
import { Skeleton } from '../../../skeletons';
import riskAssessmentService from '../../../services/carehome_manager/riskAssessmentService';
import { Select, Button } from '../../../components';

// Categories matching the new design
const riskCategories = [
    { id: 'personal_care', label: 'Personal care', icon: HeartPulse },
    { id: 'mobility', label: 'Mobility', icon: PersonStanding },
    { id: 'moving_handling', label: 'Moving and handling', icon: Move },
    { id: 'falls', label: 'Falls', icon: Activity },
    { id: 'medication', label: 'Medication', icon: Pill },
    { id: 'nutrition_hydration', label: 'Nutrition and hydration', icon: Utensils },
    { id: 'skin_integrity', label: 'Skin integrity', icon: Bandage },
    { id: 'continence', label: 'Continence', icon: Droplets },
    { id: 'behaviour_wellbeing', label: 'Behaviour and emotional wellbeing', icon: Brain },
    { id: 'mental_capacity', label: 'Mental capacity and consent', icon: Scale },
    { id: 'safeguarding', label: 'Safeguarding', icon: ShieldCheck },
    { id: 'environment_safety', label: 'Environment and home safety', icon: Home },
    { id: 'fire_safety', label: 'Fire safety', icon: Flame },
    { id: 'infection_control', label: 'Infection control', icon: ShieldAlert },
    { id: 'equipment', label: 'Equipment', icon: Wrench },
    { id: 'community_access', label: 'Community access', icon: MapPin },
    { id: 'finance', label: 'Finance and personal belongings', icon: Wallet },
    { id: 'end_of_life', label: 'End of life care', icon: HeartHandshake },
    { id: 'other', label: 'Other', icon: ClipboardList },
];

// Default assessment-type suggestions (merged with provider-saved types)
const DEFAULT_ASSESSMENT_TYPES = [
    'Falls risk assessment',
    'Moving and handling assessment',
    'Mobility assessment',
    'Medication risk assessment',
    'Nutrition and hydration assessment',
    'Skin integrity assessment',
    'Pressure area risk assessment',
    'Continence assessment',
    'Behaviour risk assessment',
    'Mental capacity assessment',
    'DOLS related risk assessment',
    'Safeguarding risk assessment',
    'Environmental risk assessment',
    'Home safety assessment',
    'Fire evacuation risk assessment',
    'PEEP assessment',
    'Infection control risk assessment',
    'Bed rails risk assessment',
    'Use of equipment assessment',
    'Community access risk assessment',
    'Personal care risk assessment',
    'Choking risk assessment',
    'Diabetes risk assessment',
    'Epilepsy risk assessment',
    'Self medication risk assessment',
    'Financial risk assessment',
    'End of life care risk assessment',
];

const STATUS_OPTIONS = ['Active', 'Under review', 'Completed', 'Archived'];

const EMPTY_FORM = {
    category: '',
    assessment_type: '',
    assessment_date: '',
    next_review_due: '',
    assessed_by: '',
    responsible_person: '',
    risk_score: '',
    risk_level: 'Low Risk',
    status: 'Active',
    review_on_condition_change: false,
    control_measures: '',
    notes: '',
};

// Read a value from a row, falling back to the legacy `details` JSON for old rows
const fieldVal = (a, col, legacyKey) => {
    if (a?.[col] !== undefined && a[col] !== null && a[col] !== '') return a[col];
    if (legacyKey && a?.details && a.details[legacyKey] !== undefined && a.details[legacyKey] !== null && a.details[legacyKey] !== '') {
        return a.details[legacyKey];
    }
    return null;
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : 'N/A');

// `focusId` (optional) — an assessment to select once loaded; set by a
// reminder link (?focus=), see utils/reminderLinks.js.
const RiskAssessmentTab = ({ residenceId, focusId = null }) => {
    const [activeAssessmentId, setActiveAssessmentId] = useState(null);
    const [assessments, setAssessments] = useState([]);
    useEffect(() => {
        if (!focusId) return;
        const hit = assessments.find((a) => String(a.id) === String(focusId));
        if (hit) setActiveAssessmentId(hit.id);
    }, [focusId, assessments]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [savedTypes, setSavedTypes] = useState([]);
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);

    const [formData, setFormData] = useState(EMPTY_FORM);

    // Merge default suggestions with provider-saved types (unique, alpha)
    const allTypes = Array.from(new Set([...DEFAULT_ASSESSMENT_TYPES, ...savedTypes])).sort((a, b) => a.localeCompare(b));
    const filteredTypes = formData.assessment_type
        ? allTypes.filter(t => t.toLowerCase().includes(formData.assessment_type.toLowerCase()))
        : allTypes;

    const fetchAssessments = async () => {
        const response = await riskAssessmentService.getRiskAssessmentsByResidence(residenceId);
        const parsed = (response.data || []).map(ra => ({
            ...ra,
            details: typeof ra.details === 'string' ? JSON.parse(ra.details) : ra.details,
        }));
        setAssessments(parsed);
        return parsed;
    };

    const fetchTypes = async () => {
        try {
            const res = await riskAssessmentService.getAssessmentTypes(residenceId);
            setSavedTypes((res.data || []).map(t => t.name));
        } catch (err) {
            // non-fatal — defaults still work
            console.error('Could not load assessment types', err);
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const parsed = await fetchAssessments();
                if (parsed.length > 0) setActiveAssessmentId(parsed[0].id);
                setError(null);
            } catch (err) {
                console.error('Error fetching risk assessments:', err);
                setError('Failed to load risk assessments');
            } finally {
                setLoading(false);
            }
        };
        if (residenceId) {
            load();
            fetchTypes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [residenceId]);

    const handleOpenModal = (isReview = false) => {
        if (isReview && activeContent) {
            setFormData({
                category: activeContent.category_name || '',
                assessment_type: fieldVal(activeContent, 'assessment_type', 'assessmentType') || '',
                assessment_date: (fieldVal(activeContent, 'assessment_date', 'lastAssessed') || '').toString().split('T')[0] || '',
                next_review_due: (fieldVal(activeContent, 'next_review_due', 'reAssessOn') || '').toString().split('T')[0] || '',
                assessed_by: fieldVal(activeContent, 'assessed_by', 'assessedBy') || '',
                responsible_person: fieldVal(activeContent, 'responsible_person', 'responsiblePerson') || '',
                risk_score: fieldVal(activeContent, 'risk_score', 'score') || '',
                risk_level: fieldVal(activeContent, 'risk_level', 'riskLevel') || 'Low Risk',
                status: fieldVal(activeContent, 'status') || 'Active',
                review_on_condition_change: !!fieldVal(activeContent, 'review_on_condition_change', 'reviewOnConditionChange'),
                control_measures: fieldVal(activeContent, 'control_measures', 'controlMeasures') || '',
                notes: fieldVal(activeContent, 'notes', 'notes') || '',
            });
            setIsCreateMode(false);
        } else {
            setFormData({ ...EMPTY_FORM, assessment_date: new Date().toISOString().split('T')[0] });
            setIsCreateMode(true);
        }
        setShowTypeDropdown(false);
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSelectType = (t) => {
        setFormData(prev => ({ ...prev, assessment_type: t }));
        setShowTypeDropdown(false);
    };

    const handleAddType = async () => {
        const name = (formData.assessment_type || '').trim();
        if (!name || savedTypes.includes(name) || DEFAULT_ASSESSMENT_TYPES.includes(name)) return;
        try {
            await riskAssessmentService.createAssessmentType(residenceId, name);
            await fetchTypes();
        } catch (err) {
            console.error('Could not save assessment type', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.category) {
            setError('Please select a category');
            return;
        }

        try {
            setIsSubmitting(true);

            const payload = {
                category_name: formData.category,
                assessment_type: formData.assessment_type || null,
                assessment_date: formData.assessment_date || null,
                next_review_due: formData.next_review_due || null,
                assessed_by: formData.assessed_by || null,
                responsible_person: formData.responsible_person || null,
                risk_score: formData.risk_score === '' ? null : formData.risk_score,
                risk_level: formData.risk_level,
                status: formData.status,
                review_on_condition_change: !!formData.review_on_condition_change,
                control_measures: formData.control_measures || null,
                notes: formData.notes || null,
            };

            if (isCreateMode) {
                await riskAssessmentService.createRiskAssessment(residenceId, payload);
            } else if (activeContent) {
                await riskAssessmentService.updateRiskAssessment(residenceId, activeContent.id, payload);
            }

            const parsed = await fetchAssessments();
            await fetchTypes();

            if (isCreateMode && parsed.length > 0) {
                setActiveAssessmentId(parsed[0].id);
            }

            setIsModalOpen(false);
            setError(null);
        } catch (err) {
            console.error('Error submitting risk assessment:', err);
            setError('Failed to save assessment');
        } finally {
            setIsSubmitting(false);
        }
    };

    const activeContent = assessments.find(ra => ra.id === activeAssessmentId) || null;
    const activeCategoryData = activeContent
        ? riskCategories.find(c => c.id === activeContent.category_name)
        : null;
    const ActiveIcon = activeCategoryData?.icon || ClipboardList;

    const riskLevelClass = (level) =>
        level === 'High Risk' ? 'text-red-500' : level === 'Medium Risk' ? 'text-amber-500' : 'text-green-500';

    const inputClass = 'w-full bg-white dark:bg-[#262b33] border border-blue-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-[14px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500';
    const labelClass = 'block text-[14px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5';

    return (
        <div className="mt-6 animate-in fade-in duration-300">

            {/* Header Section */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Risk Assessment</h2>
                <button
                    onClick={() => handleOpenModal(false)}
                    className="bg-[#2563EB] hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition shadow-sm"
                >
                    Create assessment
                </button>
            </div>

            <div className="flex flex-col md:flex-row gap-6">

                {/* Left Sidebar Menu */}
                <div className="w-full md:w-[280px] lg:w-[320px] flex-shrink-0 flex flex-col gap-2.5">
                    {loading ? (
                        <div className="flex flex-col gap-2.5">
                            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
                        </div>
                    ) : assessments.length === 0 ? (
                        <div className="bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-5 text-center">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No assessments found</p>
                        </div>
                    ) : (
                        assessments.map((assessment) => {
                            const categoryData = riskCategories.find(c => c.id === assessment.category_name);
                            const Icon = categoryData?.icon || ClipboardList;
                            const isActive = activeAssessmentId === assessment.id;

                            return (
                                <button
                                    key={assessment.id}
                                    onClick={() => setActiveAssessmentId(assessment.id)}
                                    className={`flex items-center gap-3 w-full px-5 py-3.5 rounded-xl transition text-left shadow-sm border ${isActive
                                        ? 'bg-[#2563EB] text-white border-[#2563EB]'
                                        : 'bg-[#F8FAFC] dark:bg-[#262b33] text-gray-700 dark:text-gray-300 border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                                        }`}
                                >
                                    <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-[#3B82F6] dark:text-blue-400'}`} strokeWidth={1.5} />
                                    <span className={`text-[14px] font-medium ${isActive ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                        {categoryData?.label || assessment.category_name}
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Right Content Panel */}
                <div className="flex-1 min-w-0 bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm h-fit">

                    {loading || !activeContent ? (
                        <div className="flex items-center justify-center py-16">
                            {loading ? <Skeleton className="h-40 w-full rounded-xl" /> : <p className="text-gray-500 dark:text-gray-400 text-sm">Select an assessment</p>}
                        </div>
                    ) : (
                        <>
                            {/* Panel Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                                <div className="flex items-center gap-3">
                                    <ActiveIcon className={`w-5 h-5 text-[#3B82F6] dark:text-blue-400`} strokeWidth={1.5} />
                                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100">
                                        {activeCategoryData?.label || activeContent.category_name}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => handleOpenModal(true)}
                                    className="bg-[#2563EB] hover:bg-blue-700 text-white px-6 py-1.5 rounded-md text-[13px] font-medium transition shadow-sm"
                                >
                                    Review
                                </button>
                            </div>

                            {/* Key-Value Details */}
                            <div className="px-6 py-5 flex flex-col gap-4">
                                <DetailRow label="Assessment type" value={fieldVal(activeContent, 'assessment_type', 'assessmentType') || 'N/A'} />
                                <DetailRow label="Assessment date" value={formatDate(fieldVal(activeContent, 'assessment_date', 'lastAssessed'))} />
                                <DetailRow label="Next review due" value={formatDate(fieldVal(activeContent, 'next_review_due', 'reAssessOn'))} />
                                <DetailRow label="Assessed by" value={fieldVal(activeContent, 'assessed_by', 'assessedBy') || 'N/A'} />
                                <DetailRow label="Responsible person" value={fieldVal(activeContent, 'responsible_person', 'responsiblePerson') || 'N/A'} />
                                <DetailRow label="Risk score" value={fieldVal(activeContent, 'risk_score', 'score') ?? 'N/A'} />

                                <div className="flex items-center justify-between">
                                    <span className="text-[14px] text-gray-400 dark:text-gray-500 font-medium">Risk level</span>
                                    <span className={`text-[14px] font-semibold ${riskLevelClass(fieldVal(activeContent, 'risk_level', 'riskLevel'))}`}>
                                        {fieldVal(activeContent, 'risk_level', 'riskLevel') || 'N/A'}
                                    </span>
                                </div>

                                <DetailRow label="Status" value={fieldVal(activeContent, 'status') || 'N/A'} />
                                <DetailRow
                                    label="Review if condition changes"
                                    value={fieldVal(activeContent, 'review_on_condition_change', 'reviewOnConditionChange') ? 'Yes' : 'No'}
                                />
                            </div>

                            <div className="border-t border-gray-100 dark:border-gray-800 mx-6"></div>

                            {/* Control measures */}
                            <div className="px-6 pt-5">
                                <h4 className="text-[14px] font-semibold text-gray-800 dark:text-gray-100 mb-2">Control measures / actions</h4>
                                <p className="text-[13.5px] text-gray-500 dark:text-gray-400 leading-relaxed text-justify">
                                    {fieldVal(activeContent, 'control_measures', 'controlMeasures') || 'No control measures provided.'}
                                </p>
                            </div>

                            {/* Notes Section */}
                            <div className="p-6">
                                <h4 className="text-[14px] font-semibold text-gray-800 dark:text-gray-100 mb-2">Notes</h4>
                                <p className="text-[13.5px] text-gray-500 dark:text-gray-400 leading-relaxed text-justify">
                                    {fieldVal(activeContent, 'notes', 'notes') || 'No assessment notes provided.'}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Create/Review Assessment Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={isCreateMode ? "Create Risk Assessment" : "Review Risk Assessment"}
                maxWidth="max-w-[560px]"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Category */}
                    <div>
                        <label className={labelClass}>Category</label>
                        <Select
                            value={formData.category}
                            onChange={(v) => setFormData((prev) => ({ ...prev, category: v }))}
                            disabled={!isCreateMode}
                            placeholder="Select category"
                            options={riskCategories.map((cat) => ({ value: String(cat.id), label: cat.label }))}
                            className="w-full"
                        />
                    </div>

                    {/* Assessment type (autocomplete + add) */}
                    <div>
                        <label className={labelClass}>Assessment type</label>
                        <div className="relative">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        name="assessment_type"
                                        autoComplete="off"
                                        value={formData.assessment_type}
                                        onChange={(e) => { handleInputChange(e); setShowTypeDropdown(true); }}
                                        onFocus={() => setShowTypeDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowTypeDropdown(false), 150)}
                                        placeholder="Search or add assessment type"
                                        className={inputClass}
                                    />
                                    {showTypeDropdown && filteredTypes.length > 0 && (
                                        <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                            {filteredTypes.map(t => (
                                                <button
                                                    type="button"
                                                    key={t}
                                                    onMouseDown={() => handleSelectType(t)}
                                                    className="w-full text-left px-4 py-2 text-[13.5px] text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-800"
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddType}
                                    className="flex items-center gap-1 px-3 rounded-lg bg-blue-50 dark:bg-gray-800 text-[13px] font-medium text-[#2563EB] dark:text-blue-400 hover:bg-blue-100"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Add
                                </button>
                            </div>
                        </div>
                        <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-1">Saved assessment types can be reused by this provider.</p>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Assessment date</label>
                            <input type="date" name="assessment_date" value={formData.assessment_date} onChange={handleInputChange} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Next review due</label>
                            <input type="date" name="next_review_due" value={formData.next_review_due} onChange={handleInputChange} className={inputClass} />
                        </div>
                    </div>

                    {/* People */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Assessed by</label>
                            <div className="relative">
                                <input type="text" name="assessed_by" value={formData.assessed_by} onChange={handleInputChange} placeholder="Name of assessor" className={`${inputClass} pl-10`} />
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Responsible person</label>
                            <input type="text" name="responsible_person" value={formData.responsible_person} onChange={handleInputChange} placeholder="Name or role" className={inputClass} />
                        </div>
                    </div>

                    {/* Score + Level */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Risk score</label>
                            <input type="number" name="risk_score" value={formData.risk_score} onChange={handleInputChange} placeholder="e.g. 26" className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Risk level</label>
                            <Select
                                value={formData.risk_level}
                                onChange={(v) => setFormData((prev) => ({ ...prev, risk_level: v }))}
                                options={[
                                    { value: 'Low Risk', label: 'Low Risk' },
                                    { value: 'Medium Risk', label: 'Medium Risk' },
                                    { value: 'High Risk', label: 'High Risk' },
                                ]}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Status + Review-on-change */}
                    <div className="grid grid-cols-2 gap-4 items-center">
                        <div>
                            <label className={labelClass}>Status</label>
                            <Select
                                value={formData.status}
                                onChange={(v) => setFormData((prev) => ({ ...prev, status: v }))}
                                options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                                className="w-full"
                            />
                        </div>
                        <label className="flex items-center gap-2 mt-6 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                name="review_on_condition_change"
                                checked={formData.review_on_condition_change}
                                onChange={handleInputChange}
                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-[#2563EB] focus:ring-blue-500"
                            />
                            <span className="text-[14px] text-gray-700 dark:text-gray-300">Review if condition changes</span>
                        </label>
                    </div>

                    {/* Control measures */}
                    <div>
                        <label className={labelClass}>Control measures / actions</label>
                        <textarea
                            name="control_measures"
                            value={formData.control_measures}
                            onChange={handleInputChange}
                            placeholder="What actions or measures are in place to reduce the risk?"
                            rows={3}
                            className={`${inputClass} resize-none`}
                        />
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {['Example: supervise transfers', 'Example: walking frame nearby', 'Example: review after fall'].map(ex => (
                                <span key={ex} className="text-[11px] text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-full px-2 py-0.5">{ex}</span>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className={labelClass}>Notes</label>
                        <div className="relative">
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Additional notes or comments..."
                                rows={3}
                                className={`${inputClass} resize-none`}
                            />
                            <Sparkles className="absolute right-3 top-3 w-4 h-4 text-blue-500 opacity-60" />
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <Button type="submit" variant="primary" loading={isSubmitting}>
                            Submit
                        </Button>
                    </div>
                </form>
            </Modal>

            {error && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 rounded-lg text-sm">{error}</div>}
        </div>
    );
};

// Small presentational helper for label/value rows
const DetailRow = ({ label, value }) => (
    <div className="flex items-center justify-between">
        <span className="text-[14px] text-gray-400 dark:text-gray-500 font-medium">{label}</span>
        <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-200">{value}</span>
    </div>
);

export default RiskAssessmentTab;
